import React, { useState, useEffect } from 'react';
import {
  getTemplates,
  getTemplateHistory,
  getSnapshot,
  refreshTemplates,
  TemplateSummary,
  TemplateSnapshot
} from '../../api/templates';
import { PageHeader } from '../ui';

const TemplateSnapshotHistory: React.FC = () => {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [history, setHistory] = useState<TemplateSnapshot[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<TemplateSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTemplates();
      setTemplates(data);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (templateId: string) => {
    try {
      setHistoryLoading(true);
      setSelectedTemplate(templateId);
      setSelectedSnapshot(null);
      const data = await getTemplateHistory(templateId);
      setHistory(data);
      if (data.length > 0) {
        setSelectedSnapshot(data[0]);
      }
    } catch (err) {
      setError('Failed to load template history');
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadSnapshotDetail = async (snapshotId: string) => {
    try {
      const data = await getSnapshot(snapshotId);
      setSelectedSnapshot(data);
    } catch (err) {
      setError('Failed to load snapshot');
      console.error(err);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const result = await refreshTemplates();
      alert(`Refreshed ${result.refreshed} templates. ${result.errors} errors.`);
      await loadTemplates();
      if (selectedTemplate) {
        await loadHistory(selectedTemplate);
      }
    } catch (err) {
      setError('Failed to refresh templates');
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTemplates = templates.filter(t =>
    t.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.templateId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <PageHeader
            title="Email Templates"
            description="View and track changes to MailerSend email templates used for automated emails."
          />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {refreshing ? 'Refreshing...' : 'Refresh from MailerSend'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex gap-6">
        {/* Left Panel - Template List */}
        <div className="w-80 flex-shrink-0">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Template Count */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Template List */}
            <div className="max-h-[600px] overflow-y-auto">
              {filteredTemplates.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {searchTerm ? 'No templates match your search' : 'No templates found'}
                </div>
              ) : (
                filteredTemplates.map((template) => (
                  <div
                    key={template.templateId}
                    onClick={() => loadHistory(template.templateId)}
                    className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedTemplate === template.templateId ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 text-sm truncate">
                      {template.templateName}
                    </div>
                    <div className="text-xs text-gray-400 font-mono mt-1">
                      {template.templateId}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {formatDate(template.lastUpdated)}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {template.versionCount} ver
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Details */}
        <div className="flex-1 min-w-0">
          {!selectedTemplate ? (
            <div className="bg-white rounded-lg border border-gray-200 h-[650px] flex items-center justify-center">
              <div className="text-center text-gray-400">
                <div className="text-4xl mb-2">📧</div>
                <p>Select a template to view details</p>
              </div>
            </div>
          ) : historyLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 h-[650px] flex items-center justify-center">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : selectedSnapshot ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Template Header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedSnapshot.templateName}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedSnapshot.subject || 'Subject uses template default'}
                    </p>
                  </div>
                  <a
                    href={`https://app.mailersend.com/templates/${selectedSnapshot.templateId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Edit in MailerSend
                  </a>
                </div>

                {/* Version Pills */}
                {history.length > 1 && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-gray-500">Versions:</span>
                    <div className="flex gap-1 flex-wrap">
                      {history.map((snapshot, index) => (
                        <button
                          key={snapshot.id}
                          onClick={() => loadSnapshotDetail(snapshot.id)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            selectedSnapshot.id === snapshot.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          v{history.length - index}
                          {index === 0 && ' (latest)'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variables */}
                {selectedSnapshot.variables && selectedSnapshot.variables.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">Variables:</span>
                    {selectedSnapshot.variables.map((v) => (
                      <span
                        key={v}
                        className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-mono"
                      >
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                )}

                {/* Metadata */}
                <div className="mt-3 text-xs text-gray-400">
                  Captured: {formatDate(selectedSnapshot.capturedAt)} ·
                  Hash: {selectedSnapshot.snapshotHash.substring(0, 12)}...
                </div>
              </div>

              {/* Preview Area */}
              <div className="h-[450px] overflow-auto bg-gray-100">
                {selectedSnapshot.htmlContent ? (
                  <iframe
                    srcDoc={selectedSnapshot.htmlContent}
                    title="Template Preview"
                    className="w-full h-full border-0 bg-white"
                    sandbox="allow-same-origin"
                  />
                ) : selectedSnapshot.imagePath ? (
                  <div className="p-4">
                    <img
                      src={selectedSnapshot.imagePath}
                      alt={`Template: ${selectedSnapshot.templateName}`}
                      className="w-full h-auto rounded shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <div className="text-3xl mb-2">📄</div>
                      <p>Preview not available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 h-[650px] flex items-center justify-center">
              <div className="text-gray-500">No snapshot available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSnapshotHistory;
